import AddIcon from '@mui/icons-material/Add';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import DoneIcon from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { usePolling } from '../../../../hooks/usePolling';
import PathContext from '../../../../shared-components/contexts/PathContext';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import { Inclusion } from '../../../../shared-types/Inclusion';
import { AlertStatus } from '../../../../shared-types/Notation/AlertStatus';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { INotationIdentifier } from '../Interfaces/INotationIdentifier';
import { INotationManagementFilters } from '../Interfaces/INotationManagementState';
import { NotationManagementState } from '../NotationManagement';
import ExportService, {
  INotationExport,
  INotationExportCriteria,
} from '../Services/ExportService';

interface IExportDownloadContainerProps {
  showExportDownloadContainer: boolean;
}

const RowLoadingContainer = () => {
  return (
    <TableRow>
      <TableCell width={'300px'}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="50%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Box display="flex" justifyContent="center">
          <Skeleton variant="rectangular" width={25} height={25} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

const StatusIndicator = (props: { status: string }) => {
  if (props.status === 'PENDING') {
    return (
      <Tooltip
        title="Large files may take a long time to process. Check this page later for updates."
        placement="top"
        arrow
      >
        <CircularProgress size={25} color="primary" />
      </Tooltip>
    );
  } else if (props.status === 'COMPLETE') {
    return (
      <Tooltip
        title="Export Ready. Click on the filename to download."
        placement="top"
        arrow
      >
        <DoneIcon color="primary" />
      </Tooltip>
    );
  } else if (props.status === 'EXPIRED') {
    return (
      <Tooltip
        title="Export Expired. Click on the filename to create a new Export with the same information."
        placement="top"
        arrow
      >
        <AlarmOffIcon color="error" />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title="An error has occured" placement="top" arrow>
        <PriorityHighIcon color="error" />
      </Tooltip>
    );
  }
};

const ExportWarningDialog = (props: {
  open: boolean;
  setOpen: (b: boolean) => void;
}) => {
  return (
    <Dialog
      open={props.open}
      onClose={() => {
        props.setOpen(false);
      }}
    >
      <DialogContent>
        <DialogContentText>
          Notation Export will only include Alerts and Updates. Any selected
          Encouragements, Data Insights, and Group Messages will not be
          reflected in the file.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="text"
          onClick={() => {
            props.setOpen(false);
          }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExportDownloadContainer = (props: IExportDownloadContainerProps) => {
  const state = useContext(NotationManagementState);
  const pathContext = useContext(PathContext);
  const [isLoading, setIsLoading] = useState(true);
  const [availableDownloads, setAvailableDownloads] = useState<
    INotationExport[]
  >([]);
  const [openDialog, setOpenDialog] = useState(false);

  const scrollTrigger = useScrollTrigger();

  const canExport = useMemo(() => {
    const applicableTypes = [NotationType.Alert, NotationType.Update];
    return (
      (state.selectAll &&
        (state.filters.notationTypes.length === 0 ||
          state.filters.notationTypes.findIndex(x =>
            applicableTypes.includes(x),
          ) !== -1)) ||
      (!state.selectAll &&
        state.selectedNotations.findIndex(x =>
          applicableTypes.includes(x.notationType),
        ) !== -1)
    );
  }, [state.filters, state.selectedNotations, state.selectAll]);

  const containsNonExportable = useMemo(() => {
    const applicableTypes = [
      NotationType.Encouragement,
      NotationType.CheckIn,
      NotationType.DataInsight,
      NotationType.GroupMessage,
    ];
    return (
      (state.selectAll &&
        (state.filters.notationTypes.length === 0 ||
          state.filters.notationTypes.findIndex(x =>
            applicableTypes.includes(x),
          ) !== -1)) ||
      (!state.selectAll &&
        state.selectedNotations.findIndex(x =>
          applicableTypes.includes(x.notationType),
        ) !== -1)
    );
  }, [state.filters, state.selectedNotations, state.selectAll]);

  useEffect(() => {
    if (props.showExportDownloadContainer) {
      let canceled = false;
      (async () => {
        const downloads = await ExportService.getExportStatuses();
        if (!canceled) {
          setAvailableDownloads(downloads);
          setIsLoading(false);
        }
      })();
      return () => {
        canceled = true;
      };
    }
    return () => {};
  }, [props.showExportDownloadContainer]);

  useEffect(() => {
    // Scroll back to top when the ExportDownloadContainer is opened
    if (props.showExportDownloadContainer && scrollTrigger) {
      const anchor = document.querySelector('#notation-management-header');

      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [props.showExportDownloadContainer]);

  usePolling(async () => {
    // Only poll while the container is open & there are pending exports
    if (
      props.showExportDownloadContainer &&
      availableDownloads.some(x => x.status === 'PENDING')
    ) {
      ExportService.getExportStatuses().then(x => {
        // Update the state of displayedDownloads
        setAvailableDownloads(availableDownloads => {
          x.forEach(exports => {
            var index = availableDownloads.findIndex(y => {
              return y.exportId === exports.exportId;
            });
            if (index !== -1) availableDownloads[index] = exports;
          });
          return [...availableDownloads];
        });
      });
    }
  }, 5000);

  const handleQueueExport = (
    filters: INotationManagementFilters,
    notations: INotation[] | INotationIdentifier[],
    restrictedTo: Inclusion,
  ) => {
    (async () => {
      const notationTypes = [];
      if (filters.notationTypes.includes(NotationType.Alert)) {
        if (filters.alertStatus.includes(AlertStatus.Lowered))
          notationTypes.push('Alert:Lowered');
        if (filters.alertStatus.includes(AlertStatus.Open))
          notationTypes.push('Alert:Open');
      }

      if (filters.notationTypes.includes(NotationType.Update))
        notationTypes.push('Update');

      const status = await ExportService.startExport({
        notationTypes: notationTypes,
        categories: filters.categories,
        createdFor: filters.createdFor,
        createdBy: filters.createdBy,
        startDate: filters.startDate,
        endDate: filters.endDate,
        assignedTo: filters.assignedTo,
        onlyUnassigned: filters.onlyUnassigned,
        ids: notations.map((x: INotation | INotationIdentifier) => ({
          id: x.id,
          notationType: x.notationType,
        })) as INotationIdentifier[],
        restrictedTo: restrictedTo,
      });
      setAvailableDownloads([status, ...availableDownloads]);
    })();
  };

  const mapCriteriaToFilters = (criteria: INotationExportCriteria) => {
    return {
      createdBy: criteria.createdByUser,
      createdFor: criteria.createdForUser,
      assignedTo: criteria.assignedTo,
      onlyUnassigned: criteria.onlyUnassigned,
      notationTypes: criteria.notationTypes,
      categories: criteria.categories,
      startDate: criteria.startDate,
      endDate: criteria.endDate,
    } as INotationManagementFilters;
  };

  const handleDownloadExport = (exportId: string, fileName: string) => {
    ExportService.getExportStatus(exportId).then(status => {
      fetch(status.download?.url ?? '')
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        });
    });
  };

  return (
    <Collapse in={props.showExportDownloadContainer}>
      <Box
        display="flex"
        height="300px"
        style={{
          overflow: 'auto',
          borderBottom: '1px solid rgb(220, 220, 220)',
        }}
        flexDirection="column"
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
          sx={{ padding: '8px' }}
        >
          <Button
            onClick={() => {
              if (containsNonExportable) setOpenDialog(true);

              handleQueueExport(
                state.filters,
                state.selectAll
                  ? state.excludedNotations
                  : state.selectedNotations,
                state.selectAll ? Inclusion.Exclude : Inclusion.Include,
              );
            }}
            startIcon={<AddIcon />}
          >
            New Export
          </Button>
        </Box>
        <Box>
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Filename</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Created On</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Expires On</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Applied Filters</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography>Status</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? [...Array(5)].map((e, i) => <RowLoadingContainer key={i} />)
                  : availableDownloads &&
                    availableDownloads.map(x => (
                      <TableRow key={x.exportId}>
                        <TableCell
                          style={{
                            maxWidth: '150px',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {x.status === 'COMPLETE' ? (
                            <a
                              href={`${pathContext}/api/management/notation/download/${x.exportId}`}
                              onClick={e => {
                                e.preventDefault();
                                handleDownloadExport(x.exportId, x.fileName);
                              }}
                            >
                              <Typography>{x.fileName}</Typography>
                            </a>
                          ) : x.status === 'EXPIRED' ? (
                            <Tooltip
                              title="Export Expired. Click here to create a new Export with the same information"
                              placement="top"
                              arrow
                            >
                              <a
                                href="#"
                                onClick={() => {
                                  handleQueueExport(
                                    mapCriteriaToFilters(x.criteria),
                                    x.criteria.ids.map(z => {
                                      return {
                                        notationType: 'Alert',
                                        id: z,
                                      } as INotationIdentifier;
                                    }),
                                    x.criteria.restrictedTo,
                                  );
                                }}
                              >
                                <Typography>{x.fileName}</Typography>
                              </a>
                            </Tooltip>
                          ) : (
                            x.fileName
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {x.createdOn.toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {x.expiresOn.toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{
                            maxWidth: '285px',
                            overflowWrap: 'break-word',
                          }}
                        >
                          <Box>
                            {x.criteria.notationTypes &&
                              x.criteria.notationTypes.length !== 0 && (
                                <Typography>{`Notation Types: ${x.criteria.notationTypes
                                  .map(x => {
                                    switch (x) {
                                      case 'Alert:Open':
                                        return 'Open Alerts';
                                      case 'Alert:Lowered':
                                        return 'Lowered Alerts';
                                      case 'Update':
                                        return 'Updates';
                                      default:
                                        return null;
                                    }
                                  })
                                  .filter(x => x !== null)
                                  .join(', ')}`}</Typography>
                              )}
                            {x.criteria.ids && x.criteria.ids.length !== 0 && (
                              <Typography>
                                {`Notations ${
                                  x.criteria.restrictedTo === Inclusion.Include
                                    ? 'selected'
                                    : 'excluded'
                                }: ${x.criteria.ids.length}`}
                              </Typography>
                            )}
                            {x.criteria.createdByUser && (
                              <Typography>
                                Created By:{' '}
                                {`${
                                  x.criteria.createdByUser.preferredFirstName ??
                                  x.criteria.createdByUser.firstName
                                } ${x.criteria.createdByUser.lastName}`}
                              </Typography>
                            )}
                            {x.criteria.createdForUser && (
                              <Typography>
                                Created For:{' '}
                                {`${
                                  x.criteria.createdForUser
                                    .preferredFirstName ??
                                  x.criteria.createdForUser.firstName
                                } ${x.criteria.createdForUser.lastName}`}
                              </Typography>
                            )}
                            {x.criteria.startDate && (
                              <Typography>
                                Start Date:{' '}
                                {x.criteria.startDate.toLocaleDateString()}
                              </Typography>
                            )}
                            {x.criteria.endDate && (
                              <Typography>
                                End Date:{' '}
                                {x.criteria.endDate.toLocaleDateString()}
                              </Typography>
                            )}
                            {x.criteria.categories &&
                              x.criteria.categories.length !== 0 && (
                                <Typography>
                                  Categories:{' '}
                                  {x.criteria.categories
                                    .map(x => x.name)
                                    .join(',')}
                                </Typography>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <StatusIndicator status={x.status} />
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
            {!isLoading && availableDownloads.length === 0 && (
              <Box
                height="200px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h2">
                  There are currently no exports.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <ExportWarningDialog open={openDialog} setOpen={setOpenDialog} />
    </Collapse>
  );
};

export default ExportDownloadContainer;
